import { useState, useEffect } from 'react';
import { m } from 'framer-motion';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import { alpha, styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Dialog, Container, Grid, Box, Typography, DialogContent, DialogContentText } from '@mui/material';
// components
import Page from '../components/Page';
import Logo from '../components/Logo';
import { MotionViewport } from '../components/animate';
import {
  PageHeader,
  ProjectCard,
  SearchBar,
  CauseDetails,
  CausesDesktopDropdown,
  DeletableChips,
} from '../sections/project_cause';
// service
import { CausesAPI } from '../services/causes.services';
import { ProjectAPI } from '../services/projects.services';

const COLOR_LIGHTBLUE = '#F5FBFF';

const ContentStyle = styled('div')(() => ({
  width: '100%',
  height: '100%',
  background: COLOR_LIGHTBLUE,
}));

const HeaderStyle = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  marginTop: theme.spacing(11),
}));

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const causeName = urlParams.get('cause') ? urlParams.get('cause') : null;

export default function PickProjectCause() {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedCauses, setSelectedCauses] = useState(causeName ? [...causeName.split(',')] : []);
  const [searchKey, setSearchKey] = useState('');
  const [causes, setCauses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [charities, setCharities] = useState([]);
  const [isLoading, setLoading] = useState(false);

  // Function to fetch causes
  const fetchCauses = async () => {
    try {
      const response = await CausesAPI.getAllCauses();
      if (response.data.records) {
        const data = response.data.records;
        const others = data.filter((c) => c.name === 'Others');
        const sortedCauses = sortBy(data.filter((c) => c.name !== 'Others'), ['name']);
        setCauses([...sortedCauses, ...others]);
      }
    } catch (error) {
      console.error('Error fetching causes:', error);
    }
  };

  // Function to fetch projects
  const fetchProjects = async (searchValue = '', filterCauses = []) => {
    try {
      setLoading(true);
      const response = await ProjectAPI.getAllProjects(searchValue, filterCauses);
      if (response.data.records) {
        setProjects(
          response.data.records.filter(
            (p) => p.isActive && !p.deleted && p.status.toLowerCase() === 'approved'
          )
        );
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch causes on initial load
  useEffect(() => {
    fetchCauses();
  }, []);

  // Fetch projects based on searchKey or selectedCauses
  useEffect(() => {
    if (searchKey) {
      fetchProjects(searchKey, selectedCauses);
    } else {
      fetchProjects('', selectedCauses);
    }
  }, [searchKey, selectedCauses]);

  // Debounced search handler
  const handleSearch = debounce((searchValue) => {
    setSearchKey(searchValue);
  }, 2000); // Debounce delay of 500ms

  return (
    <Page title="CharityPhilippines.org">
      <HeaderStyle>
        <PageHeader />
      </HeaderStyle>
      <ContentStyle>
        <Container component={MotionViewport}>
          <Grid container>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: { xs: 'column', md: 'row' },
                pl: 0,
                pt: 0,
                py: { xs: 0, md: 2 },
                px: { xs: 0, md: 2 },
                mt: { xs: 3, md: '-40px' },
                width: 1,
                boxShadow: { xs: 0, md: 3 },
                background: { xs: 'transparent', md: '#ffffff' },
                borderRadius: { xs: 0, md: 2 },
              }}
            >
              <CausesDesktopDropdown
                causes={causes}
                selectedCauses={selectedCauses}
                setSelectedCauses={setSelectedCauses}
              />
              <Typography
                component="span"
                variant="body1"
                sx={{ width: 40, display: { xs: 'none', md: 'block' }, textAlign: 'center', fontSize: 20, mx: 1.5 }}
              >
                Or
              </Typography>
              <SearchBar search={searchKey} handleSearch={(e) => handleSearch(e.target.value)} />
            </Grid>
            <Grid item xs={12} sx={{ mt: selectedCauses.length > 0 ? 3 : 1 }}>
              {selectedCauses.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                >
                  <Typography sx={{ typography: 'body2', mr: 2, minWidth: 'fit-content' }}>
                    Filter Projects by:
                  </Typography>
                  <DeletableChips
                    selectedCauses={selectedCauses}
                    deleteSelectedCause={(cause) =>
                      setSelectedCauses((prev) => prev.filter((c) => c !== cause))
                    }
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              {isLoading ? (
                <Dialog
                  PaperProps={{
                    sx: {
                      width: { md: '20%', xs: '90%' },
                      height: 200,
                      paddingTop: 5,
                      alignItems: 'center',
                    },
                  }}
                  open={isLoading}
                  aria-labelledby="responsive-dialog-title"
                >
                  <m.div
                    initial={{ rotateY: 0 }}
                    animate={{ rotateY: 360 }}
                    transition={{ duration: 2, ease: 'easeInOut', repeatDelay: 1, repeat: Infinity }}
                  >
                    <Logo disabledLink sx={{ marginTop: 3, width: 60, height: 25 }} />
                  </m.div>

                  <Box
                    component={m.div}
                    animate={{
                      scale: [1.2, 1, 1, 1.2, 1.2],
                      rotate: [270, 0, 0, 270, 270],
                      opacity: [0.25, 1, 1, 1, 0.25],
                      borderRadius: ['25%', '25%', '50%', '50%', '25%'],
                    }}
                    transition={{ ease: 'linear', duration: 3.2, repeat: Infinity }}
                    sx={{
                      marginTop: 1,
                      width: 65,
                      height: 65,
                      borderRadius: '25%',
                      position: 'absolute',
                      border: (theme) => `solid 3px ${alpha(theme.palette.primary.dark, 0.24)}`,
                    }}
                  />

                  <DialogContent>
                    <DialogContentText
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'center', md: 'center' },
                        marginTop: 4,
                      }}
                    >
                      Searching Projects...
                    </DialogContentText>
                  </DialogContent>
                </Dialog>
              ) : (
                <Box sx={{ pt: 0, pb: 5, width: 1, height: 1 }}>
                  <CauseDetails
                    resultTotal={projects.length + charities.length}
                    causeName={causeName || 'All Causes'}
                  />
                  <ProjectCard projectList={projects} charityList={charities} />
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      </ContentStyle>
    </Page>
  );
}
